import { Injectable, OnDestroy } from "@angular/core";
import { CanActivate, Router } from "@angular/router";
import { select, Store } from "@ngrx/store";
import { OpenIdConnectCacheService } from "@obg/common.verification";
import { CORE_ROUTE, CoreState, JurisdictionSelector } from "@obg/ngx.core";
import { DeviceConfigService } from "@obg/ngx.global";
import { Route, RouteConfig, RouteSelector } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { Subscription } from "rxjs";
import { filter, take, tap } from "rxjs/operators";

import { LoginFlowService } from "../flow/login-flow.service";
import { LoginUiConfigService } from "../login.config";
import { NewFlowService } from "../new-login-flow/new-flow.service";

@Injectable({ providedIn: "root" })
export class NewLoginFlowGuard implements CanActivate, OnDestroy {
	jurisdiction$$: Subscription | undefined;
	setReturnUrl$$: Subscription | undefined;

	constructor(
		private store: Store<CoreState>,
		private router: Router,
		private loginConfigService: LoginUiConfigService,
		private newFlowService: NewFlowService,
		private loginFlowService: LoginFlowService,
		private deviceConfigService: DeviceConfigService,
		private openIdConnectCacheService: OpenIdConnectCacheService
	) {}

	canActivate() {
		if (!this.jurisdiction$$) {
			this.jurisdiction$$ = this.store
				.pipe(
					select(JurisdictionSelector.get),
					ignoreNil(),
					tap(() => this.initFlowService())
				)
				.subscribe();
		}

		this.setReturnUrl$$ = this.store
			.pipe(
				select(RouteSelector.getCurrent),
				filter(
					(route) =>
						!!route &&
						route.name !== CORE_ROUTE.auth.openIdConnect &&
						route.name !== CORE_ROUTE.auth.login &&
						!this.deviceConfigService.isDesktop()
				),
				take(1),
				tap((currentRoute: Route) => this.openIdConnectCacheService.setReturnUrl(currentRoute.pathName))
			)
			.subscribe();

		return true;
	}

	ngOnDestroy() {
		this.jurisdiction$$?.unsubscribe();
		this.setReturnUrl$$?.unsubscribe();
	}

	private initFlowService() {
		const isNewLoginFlowEnabled =
			this.loginConfigService.getNewLoginFlowEnabled() && !!this.loginConfigService.getNewLoginFlowSteps().length;

		if (isNewLoginFlowEnabled) {
			const newRouterConfig = this.router.config.filter(
				(routeConfig: RouteConfig) => routeConfig.name !== CORE_ROUTE.registration.registerSuccess
			);

			this.router.resetConfig(newRouterConfig);
			this.loginFlowService.unsubscribe();
			this.newFlowService.initialize();
		} else {
			const isMobile = !this.deviceConfigService.isDesktop();
			this.newFlowService.unsubscribe();
			this.loginFlowService.initialize(isMobile);
		}
	}
}
